import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import type { Template } from '../types';

const FinancialTraining: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await apiService.getCurrentFinancialTemplate();
        
        if (response.success && response.template) {
          setTemplate(response.template);
          setError(null);
        } else {
          setError('No financial template found for your organization');
        }
      } catch (err: any) {
        console.error('Failed to fetch financial template:', err);
        
        // Handle specific error cases
        if (err.message.includes('401')) {
          setError('You are not authenticated. Please log in again.');
        } else if (err.message.includes('400')) {
          setError('Your account is not associated with an organization.');
        } else if (err.message.includes('404')) {
          setError('No financial template found for your organization.');
        } else {
          setError('Failed to load financial template. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
    window.scrollTo(0, 0);
  }, []);

  // Get template name with fallback
  const templateName = template?.name || "Financial Statement Analysis Fundamentals";

  const handleViewBalanceSheet = () => {
    navigate(`/financial/overview`);
  };

  const handleSkipToPractice = async () => {
    try {
      // const sessionResponse = await apiService.getActiveFinancialSession();
      
      // if (sessionResponse?.session?.id) {
      //   // Navigate to practice with actual session ID
      //   navigate(`/financial/practice`);
      // } else {
      //   // No active session, show message
      //   // toast.error('No active practice session found. Please start from the dashboard.');
        
      //   navigate(`/financial/practice/1`); // Temporary: navigate to session 1
      // }
      navigate(`/financial/practice`);
    } catch (error) {
      console.error('Failed to get active session:', error);
      toast.error('Unable to start practice. Please try again from the dashboard.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading financial template...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-6">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Template</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ← Back to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-2 py-8">
        {/* Header Card - matching EditTemplate styling */}
        <div className="bg-white rounded-lg shadow mb-8" style={{ height: '65px' }}>
          <div className="flex items-center justify-between h-full px-6">
            {/* Left Side - Breadcrumb */}
            <div className="flex items-center space-x-2">
              {/* Back arrow and Dashboard link */}
              <button
                onClick={() => navigate('/dashboard')}
                className="text-blue-400 hover:text-blue-600 hover:underline font-medium text-sm flex items-center space-x-1 transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Career Development Worksheets</span>
              </button>
              
              <span className="text-gray-400 text-sm">/</span>
              
              {/* Page Title */}
              <h1 className="text-lg font-bold text-gray-900">{templateName}</h1>
            </div>

            {/* Right Side - getting started */}
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Getting Started</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Main Content */}
        <div className="text-center mb-1">
          {/* Big Money Emoji */}
          <div className="text-6xl mb-6">💰</div>
          
          {/* Template Name in Double Green Border */}
          <div className="inline-block p-4 border-2 border-green-500 rounded-lg mb-8">
            <h1 className="text-2xl font-bold text-gray-900">{templateName}</h1>
          </div>
        </div>

        {/* Welcome Card */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm border border-gray-200 p-8 mb-8"> 
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            <strong>Welcome!</strong> This exercise will teach you to read and understand financial statements through hands-on practice.
          </p>
          {/* What You'll Learn */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              BACKGROUND
            </h2>
            <p>
              Literacy in financial statements and management reports is a vital skill for successfully managing a business. Yet research shows that only half of businesspeople feel confident reading financial statements.
            </p>
          </div>

          {/* How It Works */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              OUR APPROACH
            </h2>
            <p>We will introduce a <strong>three-section report template</strong> designed for clarity and ease of use. This intuitive design provides a clear snapshot of a business's performance at any point in time. Next, we will demonstrate the <strong>exclusive, two-step process</strong> for recording amounts within the report. Your understanding will be reinforced through practice, using <strong>sample transactions</strong> to be entered into our template. Finally, we will show that there are only <strong>four possible combinations</strong> for how the report's sections can be affected.</p>
            <p className='py-4'>If you practice and even master how financial data is recorded, you will gain the insight necessary to interpret report amounts confidently and leverage that information for better and more efficient decisions.</p>
            <p >We will demonstrate how our report can be modified to any level of sophistication and show its relationship to the standard financial statements commonly used in most businesses.</p>
          </div>

          {/* What to Expect */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
              OBJECTIVES
            </h2>
            <ul className="space-y-2 ml-10">
              <li className="flex items-start text-gray-700">
                <span className="text-green-500 text-xl mr-2">•</span>
                <span><strong>Control.</strong> Remove the confusion, complexity, intimidation, mystery, and ultimate indecision often experienced by businesspeople when attempting to read financial statements and management reports.</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-green-500 text-xl mr-2">•</span>
                <span><strong>Confidence.</strong> Instill confidence that only a finite number of ways exist for recording financial transactions.</span>
              </li>
              <li className="flex items-start text-gray-700">
                <span className="text-green-500 text-xl mr-2">•</span>
                <span><strong>Empowerment.</strong> Empower users to effectively challenge report amounts and make better-informed decisions based on accurate data.</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-4 max-w-md mx-auto">
          <button
            onClick={handleViewBalanceSheet}
            className="inline-flex items-center justify-center px-6 py-4 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            📖 View Balance Sheet Overview
          </button>
          
          <button
            onClick={handleSkipToPractice}
            className="inline-flex items-center justify-center px-6 py-4 border border-green-300 text-base font-medium rounded-md text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
          >
            🚀 Skip to Practice
          </button>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default FinancialTraining;